import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import {
  Users,
  RefreshCw,
  Search,
  X,
  Star,
  DollarSign,
  BarChart3,
  BookUser,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useSuppliers,
  SupplierCard,
  SupplierDetailModal,
} from '@/features/suppliers';
import type { Supplier } from '@/features/suppliers';
import { toast } from 'sonner';
import {
  SupplierStats, SupplierFormModal, type SupplierFormData,
} from './suppliers-components';

const TABS = [
  {
    id: 'directory',
    label: 'Directory',
    href: '/suppliers/directory',
    icon: BookUser,
  },
  {
    id: 'performance',
    label: 'Performance',
    href: '/suppliers/performance',
    icon: BarChart3,
  },
];

export function SuppliersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState<SupplierFormData>({
    name: '',
    contactName: '',
    email: '',
    phone: '',
    country: '',
    categories: [],
    paymentTerms: '',
    notes: '',
  });

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/performance')) return 'performance';
    return 'directory';
  }, [location.pathname]);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useSuppliers({search: searchQuery || undefined,});

  const suppliers = data?.data ?? [];
  const avgRating = suppliers.length
    ? (suppliers.reduce((sum, s) => sum + s.rating, 0) / suppliers.length).toFixed(1)
    : '0';
  const totalSpend = suppliers.reduce((sum, s) => sum + s.totalSpend, 0);

  const handleSupplierClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSupplier(null);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setFormData({
      name: supplier.name,
      contactName: supplier.contactName,
      email: supplier.contactEmail,
      phone: supplier.contactPhone,
      country: supplier.deliveryZones[0] ?? '',
      categories: supplier.categories,
      paymentTerms: '',
      notes: '',
    });
    setEditingSupplier(supplier);
    setShowDetailModal(false);
    setShowAddModal(true);
  };

  const handleViewOrders = (supplier: Supplier) => {
    setShowDetailModal(false);
    navigate(`/procurement/orders?supplier=${supplier.supplierId}`);
  };

  const handleViewPerformance = (supplier: Supplier) => {
    setShowDetailModal(false);
    navigate(`/suppliers/performance?supplier=${supplier.supplierId}`);
  };

  const handleCreatePO = (supplier: Supplier) => {
    setShowDetailModal(false);
    navigate(`/procurement/orders/new?supplier=${supplier.supplierId}`);
  };

  const handleAddSupplier = () => {
    setFormData({
      name: '',
      contactName: '',
      email: '',
      phone: '',
      country: '',
      categories: [],
      paymentTerms: '',
      notes: '',
    });
    setEditingSupplier(null);
    setShowAddModal(true);
  };

  const handleSaveSupplier = () => {
    if (!formData.name || !formData.email || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (editingSupplier) {
      toast.success(`Updated ${formData.name}`);
    } else {
      toast.success(`Created ${formData.name}`);
    }
    setShowAddModal(false);
    setEditingSupplier(null);
    refetch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Suppliers</h1>
          <p className="text-muted-foreground">
            Manage your supplier network and performance
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />{' '}
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'performance' ? (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Suppliers
                </CardTitle>
                <Users className="h-5 w-5 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{suppliers.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Avg Rating
                </CardTitle>
                <Star className="h-5 w-5 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{avgRating}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Spend
                </CardTitle>
                <DollarSign className="h-5 w-5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ${(totalSpend / 1000000).toFixed(1)}M
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  On-Time Delivery
                </CardTitle>
                <BarChart3 className="h-5 w-5 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">94%</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Supplier Performance Rankings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="py-3 text-left font-medium">Rank</th>
                      <th className="py-3 text-left font-medium">Supplier</th>
                      <th className="py-3 text-right font-medium">Rating</th>
                      <th className="py-3 text-right font-medium">On-Time %</th>
                      <th className="py-3 text-right font-medium">Total Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...suppliers]
                      .sort((a, b) => b.rating - a.rating)
                      .slice(0, 10)
                      .map((supplier, idx) => (
                        <tr key={supplier.supplierId} className="border-b">
                          <td className="py-3">{idx + 1}</td>
                          <td className="py-3 font-medium">{supplier.name}</td>
                          <td className="py-3 text-right">
                            <span className="flex items-center justify-end gap-1">
                              <Star className="h-3 w-3 text-amber-500" />
                              {supplier.rating.toFixed(1)}
                            </span>
                          </td>
                          <td className="py-3 text-right">
                            {Math.round(85 + supplier.rating * 3)}%
                          </td>
                          <td className="py-3 text-right">
                            ${supplier.totalSpend.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Add Supplier Button */}
          <div className="flex justify-start">
            <Button onClick={handleAddSupplier}>+ Add Supplier</Button>
          </div>

          {/* Add/Edit Supplier Modal */}
          <SupplierFormModal
            isOpen={showAddModal}
            isEditing={editingSupplier !== null}
            formData={formData}
            onFormChange={setFormData}
            onClose={() => {
              setShowAddModal(false);
              setEditingSupplier(null);
            }}
            onSave={handleSaveSupplier}
          />

          {/* Supplier Detail Modal */}
          <SupplierDetailModal
            supplier={selectedSupplier}
            isOpen={showDetailModal}
            onClose={handleCloseDetailModal}
            onEdit={handleEditSupplier}
            onViewOrders={handleViewOrders}
            onViewPerformance={handleViewPerformance}
            onCreatePO={handleCreatePO}
          />

          {/* Stats Cards */}
          <SupplierStats suppliers={suppliers} />

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search suppliers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`skel-supplier-${i.toString()}`}
                  className="h-64 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          )}

          {/* Error State */}
          {!isLoading && error && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="mb-4 h-12 w-12 text-red-500" />
                <p className="text-lg font-medium">Failed to load suppliers</p>
                <Button variant="outline" className="mt-4" onClick={() => refetch()}>
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!isLoading && !error && suppliers.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-lg font-medium">No suppliers found</p>
              </CardContent>
            </Card>
          )}

          {/* Supplier Cards Grid */}
          {!isLoading && !error && suppliers.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {suppliers.map((supplier) => (
                <SupplierCard
                  key={supplier.supplierId}
                  supplier={supplier}
                  onClick={() => handleSupplierClick(supplier)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
